import React from 'react';
import { AlertCircle, CheckCircle2, Clock3, Download, Eye, FileSpreadsheet, Loader2, RefreshCw, Send, X, XCircle } from 'lucide-react';
import { summativeExamService } from '../../../services/summativeExamService';
import { systemService } from '../../../services/systemService';
import { hierarchyService } from '../../../services/hierarchyService';
import { downloadConsolidatedSummativeMarksheetPdf } from '../utils/consolidatedSummativeMarksheetPdf';
import { downloadSenateResultsPackagePdf, senateCandidateRows, senateGroupSummary, senateOutcomeLabels } from '../utils/senateResultsPackagePdf';
import { formatAttendance, getSummativeCompetency, summativeCompetencyKey } from '../utils/summativeCompetency';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const statusLabels = {
  submitted_to_hod: 'Awaiting HoD approval', hod_approved: 'Ready for Dean presentation',
  presented_to_senate: 'Senate meeting pending Dean record', senate_approved: 'Ready for Dean release',
  released: 'Released to students', rejected: 'Returned for correction', draft: 'Draft',
};

const statusTone = status => ({
  submitted_to_hod: 'bg-amber-50 text-amber-800 border-amber-200', hod_approved: 'bg-blue-50 text-blue-800 border-blue-200',
  presented_to_senate: 'bg-violet-50 text-violet-800 border-violet-200', senate_approved: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  released: 'bg-emerald-100 text-emerald-900 border-emerald-300', rejected: 'bg-rose-50 text-rose-800 border-rose-200',
}[status] || 'bg-slate-50 text-slate-700 border-slate-200');

const snapshotRows = batch => Array.isArray(batch?.marks_snapshot) ? batch.marks_snapshot : [];
// Approval sheets are scoped to one programme, class/module and period. Use
// the strongest programme identifier available so legacy rows without a UUID
// cannot accidentally be merged into another programme's sheet.
const programScopeKey = batch => String(batch?.program_id || batch?.program_code || batch?.program_name || 'programme');
const groupKey = batch => [programScopeKey(batch), batch.program_level || 0, batch.module_number || 0, batch.academic_year_id, batch.semester_id].join(':');

// Convert unit-level approval batches into one class/module sheet: one row per
// student and one grouped block per unit (FA/60, SA/40, TT, AT% and remarks).
const consolidate = (batches, unitCatalogByGroup = {}) => {
  const groups = new Map();
  batches.forEach(batch => {
    const key = groupKey(batch);
    const group = groups.get(key) || {
      key,
      programId: batch.program_id || programScopeKey(batch),
      programFilterKey: programScopeKey(batch),
      programCode: batch.program_code,
      departmentName: batch.department_name || 'Department not specified',
      facultyName: batch.faculty_name
        || batch.facultyName
        || batch.faculty?.name
        || batch.department?.faculty?.name
        || batch.program?.department?.faculty?.name
        || 'Faculty / School not specified',
      academicYearId: batch.academic_year_id,
      semesterId: batch.semester_id,
      programName: batch.program_name || batch.program_code || 'Programme',
      programLevel: batch.program_level,
      moduleNumber: batch.module_number,
      period: `${batch.academic_year_name || 'Year'} · ${batch.semester_name || 'Period'}`,
      batches: [], units: new Map(), students: new Map(),
    };
    (unitCatalogByGroup[key] || []).forEach(unit => {
      const unitKey = String(unit.unit_code || unit.code || unit.unit_id || unit.id);
      if (unitKey && unitKey !== 'undefined') group.units.set(unitKey, {
        key: unitKey, code: unit.unit_code || unit.code || 'Unit', name: unit.unit_name || unit.name || '',
      });
    });
    group.batches.push(batch);
    const unitKey = String(batch.unit_code || batch.unit_id);
    group.units.set(unitKey, { key: unitKey, code: batch.unit_code || 'Unit', name: batch.unit_name || '' });
    snapshotRows(batch).forEach(row => {
    const studentKey = String(row.student_id || row.admission_number || row.student_name || 'student');
    const student = group.students.get(studentKey) || {
        key: studentKey,
        studentId: row.student_id,
        admissionNumber: row.admission_number,
        name: row.student_name,
        status: row.status || row.student_status,
        enrollmentStatus: row.enrollment_status,
        marks: new Map(),
      };
      if (row.status || row.student_status) student.status = row.status || row.student_status;
      if (row.enrollment_status) student.enrollmentStatus = row.enrollment_status;
      student.marks.set(unitKey, row);
      group.students.set(studentKey, student);
    });
    groups.set(key, group);
  });
  return [...groups.values()].map(group => ({
    ...group,
    units: [...group.units.values()].sort((a, b) => a.code.localeCompare(b.code)),
    students: [...group.students.values()].sort((a, b) => String(a.admissionNumber || a.name).localeCompare(String(b.admissionNumber || b.name))),
  }));
};

const normalizeValue = value => String(value ?? '').trim().toLowerCase();

// Formal deferrals do not always have a booking or a marks snapshot. Add them
// to the matching programme/level/module section (or create a section when a
// whole class was deferred) so Senate still accounts for the candidate.
const mergeDeferredCandidates = (groups, deferredRows = []) => {
  const nextGroups = groups.map(group => ({
    ...group,
    students: [...(group.students || [])],
  }));
  const sameDimension = (left, right) => left == null || right == null || Number(left) === Number(right);
  const matchesGroup = (group, row) => {
    const groupProgramValues = [group.programId, group.programCode, group.programFilterKey, group.programName]
      .map(normalizeValue)
      .filter(Boolean);
    const rowProgramValues = [row.program_id, row.program_code, row.program_name].map(normalizeValue).filter(Boolean);
    const programMatches = !rowProgramValues.length || rowProgramValues.some(value => groupProgramValues.includes(value));
    return programMatches
      && sameDimension(group.programLevel, row.program_level)
      && sameDimension(group.moduleNumber, row.module_number)
      && (!row.academic_year_id || String(group.academicYearId) === String(row.academic_year_id))
      && (!row.semester_id || String(group.semesterId) === String(row.semester_id));
  };
  (deferredRows || []).forEach(row => {
    let group = nextGroups.find(candidate => matchesGroup(candidate, row));
    if (!group) {
      group = {
        key: `deferred:${row.student_id}:${row.academic_year_id || 'year'}:${row.semester_id || 'semester'}:${row.program_id || row.program_code || row.program_name || 'programme'}:${row.program_level || 0}:${row.module_number || 0}`,
        programId: row.program_id || row.program_code || row.program_name,
        programFilterKey: row.program_id || row.program_code || row.program_name,
        programCode: row.program_code,
        departmentName: row.department_name || 'Department not specified',
        facultyName: row.faculty_name || 'Faculty / School not specified',
        academicYearId: row.academic_year_id,
        semesterId: row.semester_id,
        programName: row.program_name || row.program_code || 'Programme',
        programLevel: row.program_level,
        moduleNumber: row.module_number,
        period: `${row.academic_year_name || 'Year'} · ${row.semester_name || 'Period'}`,
        batches: [],
        units: [],
        students: [],
      };
      nextGroups.push(group);
    }
    const existing = group.students.find(student => (
      (row.student_id && String(student.studentId) === String(row.student_id))
      || (row.admission_number && String(student.admissionNumber) === String(row.admission_number))
    ));
    if (existing) {
      existing.status = 'deferred';
      existing.enrollmentStatus = 'deferred';
      return;
    }
    group.students.push({
      key: `deferred-student:${row.student_id || row.admission_number || row.student_name}`,
      studentId: row.student_id,
      admissionNumber: row.admission_number,
      name: row.student_name,
      status: 'deferred',
      enrollmentStatus: 'deferred',
      marks: new Map(),
    });
  });
  return nextGroups.map(group => ({
    ...group,
    students: [...group.students].sort((a, b) => String(a.admissionNumber || a.name).localeCompare(String(b.admissionNumber || b.name))),
  }));
};

// Result snapshots historically contained only candidates who had booked (and
// in some installations, paid for) an examination. Senate must account for
// every active admitted learner in the programme/module, including learners
// with no booking or marks. Merge the complete registration roster returned by
// the Senate roster RPC into the approved result groups before generating the
// packet.
const mergeAdmittedRosterCandidates = (groups, rosterRows = []) => {
  const sameDimension = (left, right) => left == null || right == null || String(left) === String(right);
  const nextGroups = groups.map(group => ({
    ...group,
    units: [...(group.units || [])],
    students: [...(group.students || [])].map(student => ({
      ...student,
      _fromApprovedSnapshot: true,
      marks: student.marks instanceof Map ? new Map(student.marks) : new Map(),
    })),
  }));
  const matchesGroup = (group, row) => {
    const groupProgramValues = [group.programId, group.programCode, group.programFilterKey, group.programName]
      .map(normalizeValue)
      .filter(Boolean);
    const rowProgramValues = [row.program_id, row.program_code, row.program_name]
      .map(normalizeValue)
      .filter(Boolean);
    const programMatches = !rowProgramValues.length || rowProgramValues.some(value => groupProgramValues.includes(value));
    return programMatches
      && sameDimension(group.academicYearId, row.academic_year_id)
      && sameDimension(group.semesterId, row.semester_id)
      && sameDimension(group.programLevel, row.program_level)
      && sameDimension(group.moduleNumber, row.module_number);
  };

  (rosterRows || []).forEach(row => {
    let group = nextGroups.find(candidate => matchesGroup(candidate, row));
    if (!group) {
      group = {
        key: `admitted:${row.academic_year_id || 'year'}:${row.semester_id || 'semester'}:${row.program_id || row.program_code || row.program_name || 'programme'}:${row.program_level || 0}:${row.module_number || 0}`,
        programId: row.program_id || row.program_code || row.program_name,
        programFilterKey: row.program_id || row.program_code || row.program_name,
        programCode: row.program_code,
        departmentName: row.department_name || 'Department not specified',
        facultyName: row.faculty_name || 'Faculty / School not specified',
        academicYearId: row.academic_year_id,
        semesterId: row.semester_id,
        programName: row.program_name || row.program_code || 'Programme',
        programLevel: row.program_level,
        moduleNumber: row.module_number,
        period: `${row.academic_year_name || 'Year'} · ${row.semester_name || 'Period'}`,
        batches: [],
        units: [],
        students: [],
      };
      nextGroups.push(group);
    }

    const unitKey = String(row.unit_code || row.unit_id || 'Unit');
    if (!group.units.some(unit => String(unit.key) === unitKey)) {
      group.units.push({ key: unitKey, code: row.unit_code || 'Unit', name: row.unit_name || '' });
    }

    // A result group may have a catalogue entry for units whose batch has not
    // yet reached Senate. Do not let those future/unsubmitted units turn an
    // already marked failed candidate into an abscondment. Only admitted
    // roster rows for units represented by an approval batch are outcome
    // evidence; when a group has no batch (a roster-only group), use all rows.
    const submittedUnitKeys = new Set((group.batches || [])
      .map(batch => String(batch.unit_code || batch.unit_id || ''))
      .filter(Boolean));
    const isSubmittedUnit = submittedUnitKeys.size === 0 || submittedUnitKeys.has(unitKey);

    const studentKey = String(row.student_id || row.admission_number || row.student_name || 'student');
    const student = group.students.find(candidate => (
      (row.student_id && String(candidate.studentId) === String(row.student_id))
      || (row.admission_number && String(candidate.admissionNumber) === String(row.admission_number))
      || (!row.student_id && !row.admission_number && normalizeValue(candidate.name) === normalizeValue(row.student_name))
    ));
    const mark = {
      ...row,
      unit_code: row.unit_code || unitKey,
      unit_name: row.unit_name || '',
      raw_marks: row.raw_marks == null ? null : row.raw_marks,
      converted_marks: row.converted_marks == null ? null : row.converted_marks,
      formative_total_60: row.formative_total_60 == null ? 0 : row.formative_total_60,
      attendance_percentage: row.attendance_percentage == null ? 0 : row.attendance_percentage,
      remarks: row.remarks || 'ABSENT / INCOMPLETE EXAM',
    };
    if (!student && !isSubmittedUnit) return;
    if (!student) {
      group.students.push({
        key: studentKey,
        studentId: row.student_id,
        admissionNumber: row.admission_number,
        name: row.student_name,
        status: row.enrollment_status,
        enrollmentStatus: row.enrollment_status,
        _fromApprovedSnapshot: false,
        marks: new Map([[unitKey, mark]]),
      });
      return;
    }
    if (row.enrollment_status) {
      student.status = row.enrollment_status;
      student.enrollmentStatus = row.enrollment_status;
    }
    if (!isSubmittedUnit) return;
    const existingMark = student.marks.get(unitKey);
    if (!existingMark || (existingMark.raw_marks == null && mark.raw_marks != null)) {
      student.marks.set(unitKey, mark);
    }
  });

  // A newly admitted learner may have no row at all in the submitted
  // snapshots. Give that learner an explicit missing-exam row for every
  // submitted unit so the Senate package classifies them as absconded rather
  // than silently treating an empty marks map as competent.
  nextGroups.forEach(group => {
    const submittedUnitKeys = new Set((group.batches || [])
      .map(batch => String(batch.unit_code || batch.unit_id || ''))
      .filter(Boolean));
    if (!submittedUnitKeys.size) return;
    const unitByKey = new Map((group.units || []).map(unit => [String(unit.key), unit]));
    group.students.forEach(student => {
      // Preserve an existing marked candidate's classification, but repair a
      // legacy snapshot row that contains no marks at all.
      if (student._fromApprovedSnapshot && student.marks.size > 0) return;
      submittedUnitKeys.forEach(unitKey => {
        if (student.marks.has(unitKey)) return;
        const unit = unitByKey.get(unitKey);
        student.marks.set(unitKey, {
          unit_code: unit?.code || unitKey,
          unit_name: unit?.name || '',
          raw_marks: null,
          converted_marks: null,
          formative_total_60: 0,
          attendance_percentage: 0,
          remarks: 'ABSENT / INCOMPLETE EXAM',
        });
      });
    });
  });

  return nextGroups.map(group => ({
    ...group,
    units: [...group.units].sort((a, b) => String(a.code).localeCompare(String(b.code))),
    students: [...group.students]
      .map(({ _fromApprovedSnapshot, ...student }) => student)
      .sort((a, b) => String(a.admissionNumber || a.name).localeCompare(String(b.admissionNumber || b.name))),
  }));
};

const SummativeResultApprovals = () => {
  const { profile } = useAuth();
  const assignedRoles = [profile?.primary_role, ...(profile?.secondary_roles || [])].filter(Boolean);
  const senateAudience = assignedRoles.some(role => [
    'senate', 'senate_member', 'senate_secretary',
    'deputy_principal_academic', 'quality_assurance_officer', 'chief_principal',
  ].includes(role));
  const hodAudience = assignedRoles.some(role => ['head_of_department', 'head_of_section'].includes(role));
  const deanAudience = assignedRoles.includes('dean_of_faculty');
  const packageAudience = senateAudience || hodAudience || deanAudience;
  const [batches, setBatches] = React.useState([]);
  const [unitCatalogByGroup, setUnitCatalogByGroup] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [institution, setInstitution] = React.useState(null);
  const [previewGroup, setPreviewGroup] = React.useState(null);
  const [downloading, setDownloading] = React.useState(false);
  const [senatePackageDownloading, setSenatePackageDownloading] = React.useState(false);
  const [senatePackagePreview, setSenatePackagePreview] = React.useState(null);
  const [senateBoardNumber, setSenateBoardNumber] = React.useState('');
  const [workingKey, setWorkingKey] = React.useState(null);
  const [notice, setNotice] = React.useState(null);
  const [status, setStatus] = React.useState('all');
  const [returnDialog, setReturnDialog] = React.useState(null);
  const [returnReason, setReturnReason] = React.useState('');
  const [programFilter, setProgramFilter] = React.useState('all');
  const [moduleFilter, setModuleFilter] = React.useState('all');
  const [levelFilter, setLevelFilter] = React.useState('all');

  React.useEffect(() => {
    // Senate users should land on the material formally presented for a
    // Senate decision. They can still switch to another stage when auditing
    // the workflow history.
    if (senateAudience && status === 'all') setStatus('presented_to_senate');
  }, [senateAudience, status]);

  const load = React.useCallback(async () => {
    setLoading(true); setNotice(null);
    try {
      const [payload, institutionDetails] = await Promise.all([
        summativeExamService.getResultApprovalQueue({ status: status === 'all' ? null : status }),
        systemService.getInstitutionDetails(),
      ]);
      const nextBatches = Array.isArray(payload?.batches) ? payload.batches : [];
      if (!nextBatches.length && hodAudience) {
        setNotice({ type: 'error', text: 'No result groups were returned for your HoD scope. Confirm that your user profile department and the programme department are assigned to the same department.' });
      }
      const nextGroups = consolidate(nextBatches);
      const catalogEntries = await Promise.all(nextGroups.map(async group => {
        try {
          const catalog = await summativeExamService.getResultUnitCatalog({
            academicYearId: group.academicYearId,
            semesterId: group.semesterId,
            programId: group.programId,
            programLevel: group.programLevel,
            moduleNumber: group.moduleNumber,
          });
          return [group.key, Array.isArray(catalog?.units) ? catalog.units : []];
        } catch (_) {
          return [group.key, []];
        }
      }));
      setBatches(nextBatches);
      setUnitCatalogByGroup(Object.fromEntries(catalogEntries));
      setInstitution(institutionDetails);
      setSenateBoardNumber(current => current || String(
        institutionDetails?.senate_board_number
          ?? institutionDetails?.senate_session_number
          ?? institutionDetails?.senate_board_no
          ?? '',
      ));
    } catch (error) {
      setNotice({ type: 'error', text: error?.message || 'Unable to load the result approval queue.' });
    } finally {
      setLoading(false);
    }
  }, [status]);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => () => {
    if (senatePackagePreview?.url) URL.revokeObjectURL(senatePackagePreview.url);
  }, [senatePackagePreview]);

  const downloadGroup = async group => {
    setDownloading(true);
    try { await downloadConsolidatedSummativeMarksheetPdf({ institution, group }); }
    catch (error) { setNotice({ type: 'error', text: error?.message || 'Unable to download the consolidated marksheet.' }); }
    finally { setDownloading(false); }
  };

  const downloadSenatePackage = async () => {
    if (senatePackagePreview?.url) {
      const anchor = document.createElement('a');
      anchor.href = senatePackagePreview.url;
      anchor.download = senatePackagePreview.fileName;
      anchor.click();
      return;
    }
    await previewSenatePackage();
  };

  const previewSenatePackage = async () => {
    if (!groups.length) {
      setNotice({ type: 'error', text: 'There are no result groups available for the Senate package.' });
      return;
    }
    setSenatePackageDownloading(true);
    setNotice(null);
    try {
      let signatory = null;
      let senateApprover = null;
      let abscondmentCounts = {};
      let deferredRows = [];
      let admittedRosterRows = [];
      let resolvedFacultyName = groups.find(group => group.facultyName && !/not specified/i.test(group.facultyName))?.facultyName
        || profile?.faculty_name
        || profile?.facultyName
        || profile?.faculty?.name
        || '';
      try {
        const { data: senateRows, error: senateError } = await supabase.rpc('get_senate_institution_signatories');
        if (senateError) throw senateError;
        const senateRow = (senateRows || [])[0];
        if (senateRow?.full_name) {
          senateApprover = {
            name: senateRow.full_name,
            role: senateRow.role_label || senateRow.role_name || 'Vice-Chancellor',
          };
        }
      } catch (_) {
        // Keep institution-configured fallback fields when the migration has
        // not yet been applied in an older environment.
        const currentRole = assignedRoles.map(role => String(role).toLowerCase()).find(role => ['vice_chancellor', 'vc', 'principal', 'chief_principal'].includes(role));
        if (profile?.full_name && ['vice_chancellor', 'vc', 'principal', 'chief_principal'].includes(currentRole)) {
          senateApprover = {
            name: profile.full_name,
            role: currentRole === 'chief_principal' ? 'Chief Principal' : currentRole === 'principal' ? 'Principal' : 'Vice-Chancellor',
          };
        }
      }
      try {
        const { data: abscondmentRows, error: abscondmentError } = await supabase.rpc('get_summative_abscondment_counts');
        if (abscondmentError) throw abscondmentError;
        abscondmentCounts = (abscondmentRows || []).reduce((counts, row) => {
          const count = Number(row?.abscondment_count || 0);
          if (row?.student_id) counts[`id:${row.student_id}`] = count;
          if (row?.admission_number) counts[`admission:${row.admission_number}`] = count;
          if (row?.student_name) counts[`name:${row.student_name}`] = count;
          return counts;
        }, {});
      } catch (_) {
        // Older installations without the history RPC continue using the
        // current result outcome without applying the repeat-abscondment rule.
      }
      try {
        const periods = [...new Map(groups.map(group => [
          `${group.academicYearId || ''}:${group.semesterId || ''}`,
          { academicYearId: group.academicYearId, semesterId: group.semesterId },
        ])).values()];
        const deferredResults = await Promise.all(periods.map(period => supabase.rpc('get_summative_deferred_candidates', {
          target_year: period.academicYearId || null,
          target_semester: period.semesterId || null,
        })));
        deferredRows = deferredResults.flatMap(result => result.error ? [] : (result.data || []));
      } catch (_) {
        // Older installations without the deferred-candidate RPC continue
        // using the result snapshots that are already in the approval queue.
      }
      try {
        // One roster request per programme/period/level/module keeps the
        // packet complete without issuing one request per unit. The RPC
        // returns every active admitted learner and every unit offered in
        // that module, including learners who never booked or received marks.
        const rosterRequests = [...new Map(groups.map(group => [
          `${group.academicYearId || ''}:${group.semesterId || ''}:${group.programId || group.programCode || group.programName || ''}:${group.programLevel || ''}:${group.moduleNumber || ''}`,
          group,
        ])).values()];
        const rosterResults = await Promise.all(rosterRequests.map(group => (
          summativeExamService.getAdmittedSummativeRoster({
            academicYearId: group.academicYearId,
            semesterId: group.semesterId,
            programId: group.programId || group.programCode,
            programLevel: group.programLevel,
            moduleNumber: group.moduleNumber,
          }).catch(() => ({ entries: [] }))
        )));
        admittedRosterRows = rosterResults.flatMap(result => Array.isArray(result?.entries) ? result.entries : []);
      } catch (_) {
        // The roster migration is optional for older installations. Existing
        // approval snapshots still remain usable when it is unavailable.
      }
      try {
        const groupFacultyName = groups.find(group => group.facultyName && !/not specified/i.test(group.facultyName))?.facultyName;
        const { data: signatoryRows, error: signatoryError } = await supabase.rpc('get_faculty_senate_signatories');
        if (signatoryError) throw signatoryError;
        const faculty = (signatoryRows || []).find(item => groupFacultyName && String(item?.faculty_name || '').trim() === String(groupFacultyName).trim()) || (signatoryRows || []).find(item => item?.dean_name) || (signatoryRows || [])[0];
        resolvedFacultyName = faculty?.faculty_name || '';
        if (faculty?.dean_name) {
          signatory = { name: faculty.dean_name, role: 'Dean of Faculty', faculty: faculty.faculty_name };
        }
        if (!signatory && deanAudience && profile?.full_name) {
          signatory = { name: profile.full_name, role: 'Dean of Faculty', faculty: resolvedFacultyName || groupFacultyName };
        }
      } catch (_) {
        // Keep the existing hierarchy fallback for installations before the
        // signatory RPC migration has been applied.
        try {
          const groupFacultyName = groups.find(group => group.facultyName && !/not specified/i.test(group.facultyName))?.facultyName;
          const faculties = await hierarchyService.getFaculties();
          const faculty = faculties.find(item => groupFacultyName && String(item?.name || '').trim() === String(groupFacultyName).trim()) || faculties.find(item => item?.dean?.fullName) || faculties[0];
          resolvedFacultyName = faculty?.name || '';
          if (faculty?.dean?.fullName) signatory = { name: faculty.dean.fullName, role: 'Dean of Faculty', faculty: faculty.name };
          if (!signatory && deanAudience && profile?.full_name) signatory = { name: profile.full_name, role: 'Dean of Faculty', faculty: resolvedFacultyName || groupFacultyName };
        } catch (_) {
          // Signature metadata is optional; the PDF keeps a labelled line when
          // the hierarchy service cannot be read by the current role.
        }
      }
      // Legacy approval rows may carry only a department name. Resolve that
      // department through the scoped hierarchy so the faculty heading still
      // comes from the database instead of a generic placeholder.
      if (!resolvedFacultyName) {
        try {
          const departmentName = groups.find(group => group.departmentName && !/not specified/i.test(group.departmentName))?.departmentName;
          const departments = await hierarchyService.getDepartments();
          const department = (departments || []).find(item => departmentName && String(item?.name || '').trim() === String(departmentName).trim());
          resolvedFacultyName = department?.faculty?.name || department?.faculty_name || '';
        } catch (_) {
          // The PDF utility retains its explicit faculty/school fallback.
        }
      }
      const rosterGroups = mergeAdmittedRosterCandidates(groups, admittedRosterRows);
      const senateGroups = mergeDeferredCandidates(rosterGroups, deferredRows).map(group => ({
        ...group,
        students: (group.students || []).map(student => ({
          ...student,
          abscondmentCount: abscondmentCounts[`id:${student.studentId}`]
            ?? abscondmentCounts[`admission:${student.admissionNumber}`]
            ?? abscondmentCounts[`name:${student.name}`]
            ?? 0,
        })),
      }));
      const result = await downloadSenateResultsPackagePdf({
        institution,
        groups: senateGroups,
        generatedBy: profile?.full_name || profile?.email || '',
        scope: hodAudience && !senateAudience && !deanAudience ? 'department' : 'faculty',
        signatory,
        facultyName: resolvedFacultyName,
        senateApprover,
        senateBoardNumber: senateBoardNumber.trim() || null,
        returnBlob: true,
      });
      setSenatePackagePreview({ url: URL.createObjectURL(result.blob), fileName: result.fileName });
    } catch (error) {
      setNotice({ type: 'error', text: error?.message || 'Unable to prepare the Senate package preview.' });
    } finally {
      setSenatePackageDownloading(false);
    }
  };

  const apply = async (group, action, providedNote = null) => {
    const statusForAction = {
      hod_approve: 'submitted_to_hod',
      hod_reject: 'submitted_to_hod',
      dean_present: 'hod_approved',
      dean_return: 'hod_approved',
      record_senate_approval: 'presented_to_senate',
      senate_approve: 'presented_to_senate',
      senate_reject: 'presented_to_senate',
      dean_release: 'senate_approved',
    };
    // A programme/module group can contain units at different points in the
    // workflow when a trainer submits another unit after an earlier unit was
    // released. Apply an action only to the batches at that action's stage;
    // released units must never make a new pending unit appear "mixed".
    const targetStatus = statusForAction[action];
    const targetBatches = group.batches.filter(batch => !targetStatus || batch.status === targetStatus);
    if (!targetBatches.length) return;
    const requiresReason = action.endsWith('reject') || action.endsWith('return');
    if (requiresReason && providedNote === null) {
      setReturnReason('');
      setReturnDialog({ group, action });
      return;
    }
    const note = requiresReason ? String(providedNote || '').trim() : '';
    setWorkingKey(group.key); setNotice(null);
    try {
      await Promise.all(targetBatches.map(batch => summativeExamService.advanceResultBatch({ batchId: batch.batch_id, action, note })));
      setNotice({ type: 'success', text: 'The consolidated result set was updated successfully.' });
      await load();
    } catch (error) { setNotice({ type: 'error', text: error?.message || 'Unable to update the consolidated result set.' }); }
    finally { setWorkingKey(null); }
  };

  const allGroups = React.useMemo(() => consolidate(batches, unitCatalogByGroup), [batches, unitCatalogByGroup]);
  const programmeOptions = React.useMemo(() => {
    const options = new Map();
    allGroups.forEach(group => {
      if (!options.has(group.programFilterKey)) {
        options.set(group.programFilterKey, {
          key: group.programFilterKey,
          label: group.programName || group.programCode || 'Programme',
        });
      }
    });
    return [...options.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [allGroups]);
  React.useEffect(() => {
    if (programFilter !== 'all' && !programmeOptions.some(option => option.key === programFilter)) setProgramFilter('all');
  }, [programFilter, programmeOptions]);
  const moduleOptions = React.useMemo(() => {
    const options = new Map();
    allGroups
      .filter(group => programFilter === 'all' || group.programFilterKey === programFilter)
      .forEach(group => {
        const key = String(group.moduleNumber || 0);
        if (!options.has(key)) options.set(key, { key, label: group.moduleNumber ? `Module ${group.moduleNumber}` : 'Module not specified' });
      });
    return [...options.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  }, [allGroups, programFilter]);
  React.useEffect(() => {
    if (moduleFilter !== 'all' && !moduleOptions.some(option => option.key === moduleFilter)) setModuleFilter('all');
  }, [moduleFilter, moduleOptions]);
  const levelOptions = React.useMemo(() => {
    const options = new Map();
    allGroups
      .filter(group => programFilter === 'all' || group.programFilterKey === programFilter)
      .filter(group => moduleFilter === 'all' || String(group.moduleNumber || 0) === moduleFilter)
      .forEach(group => {
        const key = String(group.programLevel || 0);
        if (!options.has(key)) options.set(key, { key, label: group.programLevel ? `Level ${group.programLevel}` : 'Level not specified' });
      });
    return [...options.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  }, [allGroups, moduleFilter, programFilter]);
  React.useEffect(() => {
    if (levelFilter !== 'all' && !levelOptions.some(option => option.key === levelFilter)) setLevelFilter('all');
  }, [levelFilter, levelOptions]);
  const groups = React.useMemo(
    () => allGroups.filter(group => (
      (programFilter === 'all' || group.programFilterKey === programFilter)
      && (moduleFilter === 'all' || String(group.moduleNumber || 0) === moduleFilter)
      && (levelFilter === 'all' || String(group.programLevel || 0) === levelFilter)
    )),
    [allGroups, levelFilter, moduleFilter, programFilter],
  );
  const groupStatus = group => {
    const statuses = [...new Set(group.batches.map(batch => batch.status))];
    return statuses.length === 1 ? statuses[0] : 'mixed';
  };
  const statusLabelFor = stage => senateAudience && stage === 'presented_to_senate'
    ? 'Awaiting Senate decision'
    : statusLabels[stage] || stage;

  const groupPreviewButtons = group => senateAudience
    ? <span className="flex flex-wrap items-center gap-1"><span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] font-semibold text-slate-600">{group.departmentName}</span><span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-1 text-[10px] font-semibold text-violet-800">Included in Senate package</span></span>
    : <span className="flex flex-wrap items-center gap-1"><span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] font-semibold text-slate-600">{group.departmentName}</span>
      <button type="button" onClick={() => setPreviewGroup(group)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"><Eye className="h-3.5 w-3.5" />View marksheet</button>
      <button type="button" onClick={() => downloadGroup(group)} disabled={downloading} className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50"><Download className="h-3.5 w-3.5" />Download</button>
    </span>;

  const actionButtons = group => {
    const stage = groupStatus(group);
    // Resolve the earliest pending stage. This keeps a partially submitted
    // consolidated programme moving in order while allowing already released
    // units to remain in the same historical group.
    const pendingStage = [
      { status: 'submitted_to_hod', approve: 'hod_approve', reject: 'hod_reject' },
      { status: 'hod_approved', approve: 'dean_present', reject: 'dean_return' },
      { status: 'presented_to_senate', approve: 'record_senate_approval' },
      { status: 'senate_approved', approve: 'dean_release' },
    ].find(item => group.batches.some(batch => batch.status === item.status));
    const targetBatches = pendingStage
      ? group.batches.filter(batch => batch.status === pendingStage.status)
      : [];
    const canAct = targetBatches.length > 0 && targetBatches.every(batch => batch.can_act);
    const disabled = workingKey === group.key || !canAct;
    if (!canAct) return <span className="text-[10px] text-slate-400">{pendingStage ? 'No action' : stage === 'released' ? 'Released' : 'No action'}</span>;
    if (pendingStage.status === 'submitted_to_hod') return <div className="flex flex-wrap gap-2"><button type="button" disabled={disabled} onClick={() => apply(group, 'hod_approve')} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"><CheckCircle2 className="h-3.5 w-3.5" />Approve pending units</button><button type="button" disabled={disabled} onClick={() => apply(group, 'hod_reject')} className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"><XCircle className="h-3.5 w-3.5" />Return pending units</button></div>;
    if (pendingStage.status === 'hod_approved') return <div className="flex flex-wrap gap-2"><button type="button" disabled={disabled} onClick={() => apply(group, 'dean_present')} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"><Send className="h-3.5 w-3.5" />Present pending units to Senate</button><button type="button" disabled={disabled} onClick={() => apply(group, 'dean_return')} className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"><XCircle className="h-3.5 w-3.5" />Return to department</button></div>;
    if (pendingStage.status === 'presented_to_senate') {
      if (senateAudience) {
        return <div className="flex flex-wrap gap-2"><button type="button" disabled={disabled} onClick={() => apply(group, 'senate_approve')} title="Approve this marks-free Senate package" className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"><CheckCircle2 className="h-3.5 w-3.5" />Approve Senate package</button><button type="button" disabled={disabled} onClick={() => apply(group, 'senate_reject')} className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"><XCircle className="h-3.5 w-3.5" />Return to faculty</button></div>;
      }
      return <button type="button" disabled={disabled} onClick={() => apply(group, 'record_senate_approval')} title="Record the Senate meeting decision for this programme" className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"><CheckCircle2 className="h-3.5 w-3.5" />Record Senate approval</button>;
    }
    if (pendingStage.status === 'senate_approved') return <button type="button" disabled={disabled} onClick={() => apply(group, 'dean_release')} title="Release this approved programme's provisional results to students" className="inline-flex items-center gap-1.5 rounded-lg bg-violet-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"><Send className="h-3.5 w-3.5" />Release results</button>;
    return <span className="text-[10px] text-slate-400">No action</span>;
  };

  const unitHeader = unit => <React.Fragment key={unit.key}>
    <th colSpan="4" className="border-l border-slate-200 px-2 py-2 text-center"><span className="font-mono">{unit.code}</span><span className="mt-0.5 block truncate normal-case font-normal text-slate-400" title={unit.name}>{unit.name}</span></th>
    <th rowSpan="2" className="border-l border-slate-200 px-2 py-2 text-center">REMARK</th>
  </React.Fragment>;

  const unitSubHeader = unit => <React.Fragment key={`${unit.key}-subhead`}>
    <th className="border-l border-slate-200 px-2 py-2 text-center">FA/60</th><th className="px-2 py-2 text-center">SA/40</th><th className="px-2 py-2 text-center">TT/100</th><th className="px-2 py-2 text-center">AT%</th>
  </React.Fragment>;

  const unitCells = (student, unit, compact = false) => {
    const mark = student.marks.get(unit.key);
    const result = mark ? getSummativeCompetency(mark) : null;
    const cellClass = compact ? 'border border-slate-800 px-2 py-1.5 text-center' : 'px-2 py-3 text-center';
    if (!mark) return <>{['FA/60', 'SA/40', 'TT/100', 'AT%', 'REMARK'].map((_, index) => <td key={`${unit.key}-empty-${index}`} className={`${cellClass} ${index === 0 ? `border-l ${compact ? 'border-slate-800' : 'border-slate-100'}` : ''} text-slate-300`}>—</td>)}</>;
    return <>
      <td className={`${cellClass} border-l ${compact ? 'border-slate-800' : 'border-slate-100'} font-semibold text-slate-700`}>{result.formative}</td>
      <td className={`${cellClass} font-semibold text-slate-900`}>{result.summative}</td>
      <td className={cellClass}>{result.total}</td>
      <td className={cellClass}>{formatAttendance(result.attendance)}%</td>
      <td className={`${cellClass} truncate text-slate-600`} title={result.remark}>{result.remark}</td>
    </>;
  };

  const renderGroupTable = group => {
    if (senateAudience) {
      const rows = senateCandidateRows(group);
      const summary = senateGroupSummary(group);
      return <>
        <div className="overflow-x-auto"><table className="min-w-[900px] w-full table-fixed text-left text-xs"><thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500"><tr><th className="w-12 px-3 py-2">SN</th><th className="w-44 px-3 py-2">Registration No.</th><th className="w-56 px-3 py-2">Candidate name</th><th className="w-36 px-3 py-2">Outcome</th><th className="px-3 py-2">Unit issues or recorded reasons</th></tr></thead><tbody className="divide-y divide-slate-100">{rows.map((row, index) => <tr key={row.key} className="hover:bg-slate-50/60"><td className="px-3 py-3 text-slate-500">{index + 1}</td><td className="px-3 py-3 font-mono text-[11px] text-slate-700">{row.admissionNumber || '—'}</td><td className="px-3 py-3 font-medium text-slate-900">{row.name || 'Unknown student'}</td><td className="px-3 py-3 font-semibold text-slate-800">{row.senateOutcome.label}</td><td className="px-3 py-3 text-slate-600">{row.senateOutcome.issues.join('; ') || '—'}</td></tr>)}</tbody></table></div>
        <div className="border-t border-slate-200 px-4 py-2 text-[10px] text-slate-500">{group.units.length} units · {group.students.length} students · {Object.entries(summary).filter(([, count]) => count > 0).map(([key, count]) => `${count} ${senateOutcomeLabels[key] || key}`).join(' · ') || 'No outcomes recorded'}.</div>
      </>;
    }
    return <><div className="overflow-x-auto"><table className="min-w-[1500px] w-full table-fixed text-left text-xs"><thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500"><tr><th rowSpan="2" className="w-12 px-3 py-2 align-middle">SN</th><th rowSpan="2" className="w-44 px-3 py-2 align-middle">Registration No.</th><th rowSpan="2" className="w-52 px-3 py-2 align-middle">Student name</th>{group.units.map(unitHeader)}</tr><tr>{group.units.map(unitSubHeader)}</tr></thead><tbody className="divide-y divide-slate-100">{group.students.map((student, index) => <tr key={student.key} className="hover:bg-slate-50/60"><td className="px-3 py-3 text-slate-500">{index + 1}</td><td className="px-3 py-3 font-mono text-[11px] text-slate-700">{student.admissionNumber || '—'}</td><td className="px-3 py-3 font-medium text-slate-900">{student.name || 'Unknown student'}</td>{group.units.map(unit => <React.Fragment key={unit.key}>{unitCells(student, unit)}</React.Fragment>)}</tr>)}</tbody></table></div><div className="border-t border-slate-200 px-4 py-2 text-[10px] text-slate-500">{group.units.length} units · {group.students.length} students · TT is FA plus SA; AT% must be above 70 for competency grading.</div></>;
  };

  return <div className="space-y-4">
    <section className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"><div><p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-violet-700">{senateAudience || deanAudience ? 'Faculty Senate results package' : hodAudience ? 'Department Senate packet' : 'Consolidated class sheets'}</p><h1 className="text-xl font-bold text-slate-950">Summative result approvals</h1></div><div className="flex flex-wrap items-end gap-2"><label className="block"><span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">Programme</span><select value={programFilter} onChange={event => { setProgramFilter(event.target.value); setModuleFilter('all'); setLevelFilter('all'); }} className="h-9 min-w-52 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium"><option value="all">All programmes</option>{programmeOptions.map(option => <option key={option.key} value={option.key}>{option.label}</option>)}</select></label><label className="block"><span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">Level</span><select value={levelFilter} onChange={event => setLevelFilter(event.target.value)} className="h-9 min-w-36 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium"><option value="all">All levels</option>{levelOptions.map(option => <option key={option.key} value={option.key}>{option.label}</option>)}</select></label><label className="block"><span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">Module</span><select value={moduleFilter} onChange={event => setModuleFilter(event.target.value)} className="h-9 min-w-36 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium"><option value="all">All modules</option>{moduleOptions.map(option => <option key={option.key} value={option.key}>{option.label}</option>)}</select></label><label className="block"><span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">Stage</span><select value={status} onChange={event => setStatus(event.target.value)} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium"><option value="all">All stages</option>{Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>{packageAudience && <label className="block"><span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">Senate number</span><input type="text" inputMode="numeric" value={senateBoardNumber} onChange={event => setSenateBoardNumber(event.target.value)} placeholder="e.g. 32" className="h-9 w-24 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium" /></label>}{packageAudience && <button type="button" onClick={previewSenatePackage} disabled={senatePackageDownloading || loading || !groups.length} className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-violet-700 px-3 py-2 text-xs font-semibold text-white hover:bg-violet-800 disabled:cursor-not-allowed disabled:opacity-50"><Eye className="h-4 w-4" />{senatePackageDownloading ? 'Preparing preview…' : hodAudience && !senateAudience && !deanAudience ? 'Senate Document' : 'Senate Document'}</button>}<button type="button" onClick={load} disabled={loading} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Refresh</button></div></section>
    {notice && <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${notice.type === 'error' ? 'border-rose-200 bg-rose-50 text-rose-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{notice.text}</div>}
    {loading ? <div className="grid min-h-44 place-items-center text-sm text-slate-500"><span className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" />Loading approval queue…</span></div> : !groups.length ? <div className="grid min-h-44 place-items-center rounded-xl border border-slate-200 bg-white px-5 text-center"><Clock3 className="h-8 w-8 text-slate-300" /><p className="mt-2 text-sm font-semibold text-slate-700">No result sets in this view</p><p className="mt-1 text-xs text-slate-500">A consolidated sheet appears after trainers submit all paid candidates for the module.</p></div> : <div className="space-y-4">{groups.map(group => { const stage = groupStatus(group); return <section key={group.key} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"><div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-4 py-3"><div><h2 className="text-sm font-bold text-slate-950">{group.programName}</h2><p className="mt-0.5 text-xs text-slate-500">Level {group.programLevel || '—'} · Module {group.moduleNumber || '—'} · {group.period}</p></div><div className="flex flex-wrap items-center justify-end gap-2"><span className={`inline-flex rounded-full border px-2 py-1 text-[10px] font-semibold ${statusTone(stage)}`}>{stage === 'mixed' ? 'Mixed unit stages' : statusLabelFor(stage)}</span>{groupPreviewButtons(group)}{actionButtons(group)}</div></div>{senateAudience ? renderGroupTable(group) : <><div className="overflow-x-auto"><table className="min-w-[1500px] w-full table-fixed text-left text-xs"><thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500"><tr><th rowSpan="2" className="w-12 px-3 py-2 align-middle">SN</th><th rowSpan="2" className="w-44 px-3 py-2 align-middle">Registration No.</th><th rowSpan="2" className="w-52 px-3 py-2 align-middle">Student name</th>{group.units.map(unitHeader)}</tr><tr>{group.units.map(unitSubHeader)}</tr></thead><tbody className="divide-y divide-slate-100">{group.students.map((student, index) => <tr key={student.key} className="hover:bg-slate-50/60"><td className="px-3 py-3 text-slate-500">{index + 1}</td><td className="px-3 py-3 font-mono text-[11px] text-slate-700">{student.admissionNumber || '—'}</td><td className="px-3 py-3 font-medium text-slate-900">{student.name || 'Unknown student'}</td>{group.units.map(unit => <React.Fragment key={unit.key}>{unitCells(student, unit)}</React.Fragment>)}</tr>)}</tbody></table></div><div className="border-t border-slate-200 px-4 py-2 text-[10px] text-slate-500">{group.units.length} units · {group.students.length} students · TT is FA plus SA; AT% must be above 70 for competency grading.</div></>}</section>; })}</div>}
    {senatePackagePreview && <div className="fixed inset-0 z-[130] bg-slate-950/80 p-3 sm:p-6"><div className="mx-auto flex h-full max-w-7xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"><div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-slate-900 px-4 py-3 text-white"><div><h2 className="text-sm font-semibold">Senate package preview</h2><p className="text-[11px] text-slate-300">Review the document before downloading it.</p></div><div className="flex items-center gap-2"><button type="button" onClick={downloadSenatePackage} disabled={senatePackageDownloading} className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold hover:bg-violet-700 disabled:opacity-60"><Download className="h-3.5 w-3.5" />Download</button><button type="button" onClick={() => setSenatePackagePreview(null)} className="rounded-lg p-1.5 text-slate-300 hover:bg-slate-700" aria-label="Close Senate package preview"><X className="h-5 w-5" /></button></div></div><iframe title="Senate results package preview" src={senatePackagePreview.url} className="min-h-0 w-full flex-1 border-0" /></div></div>}
    {previewGroup && <div className="fixed inset-0 z-[100] overflow-y-auto bg-slate-950/60 p-3 sm:p-6"><div className="mx-auto my-3 w-full max-w-7xl overflow-hidden rounded-xl bg-white shadow-2xl"><div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-slate-900 px-4 py-3 text-white"><div className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-blue-300" /><div><h2 className="text-sm font-semibold">Consolidated summative marksheet</h2><p className="text-[11px] text-slate-300">{previewGroup.programName} · Level {previewGroup.programLevel || '—'} · Module {previewGroup.moduleNumber || '—'} · {previewGroup.period}</p></div></div><div className="flex items-center gap-2"><button type="button" onClick={() => downloadGroup(previewGroup)} disabled={downloading} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold hover:bg-blue-700 disabled:opacity-60"><Download className="h-3.5 w-3.5" />{downloading ? 'Generating…' : 'Download PDF'}</button><button type="button" onClick={() => setPreviewGroup(null)} className="rounded-lg p-1.5 text-slate-300 hover:bg-slate-700" aria-label="Close marksheet"><X className="h-5 w-5" /></button></div></div><div className="max-h-[calc(100vh-9rem)] overflow-auto p-4"><div className="mb-4 text-center"><img src={institution?.logo_url || '/logo192.png'} alt="Institution logo" className="mx-auto h-14 w-auto object-contain" /><h3 className="mt-2 text-base font-bold uppercase text-slate-900">{institution?.name || 'Institution'}</h3><p className="text-xs font-semibold uppercase text-slate-600">Summative examination consolidated marksheet</p></div><div className="overflow-x-auto"><table className="w-full min-w-[1500px] table-fixed border-collapse border border-slate-800 text-xs"><thead className="bg-slate-100"><tr><th rowSpan="2" className="w-12 border border-slate-800 px-3 py-1.5 align-middle">SN</th><th rowSpan="2" className="w-44 border border-slate-800 px-3 py-1.5 align-middle">Registration No.</th><th rowSpan="2" className="w-52 border border-slate-800 px-3 py-1.5 text-left align-middle">Student Name</th>{previewGroup.units.map(unitHeader)}</tr><tr>{previewGroup.units.map(unitSubHeader)}</tr></thead><tbody>{previewGroup.students.map((student, index) => <tr key={student.key}><td className="border border-slate-800 px-3 py-1.5 text-center">{index + 1}</td><td className="border border-slate-800 px-3 py-1.5 font-mono">{student.admissionNumber || '—'}</td><td className="border border-slate-800 px-3 py-1.5">{student.name || 'Unknown student'}</td>{previewGroup.units.map(unit => <React.Fragment key={unit.key}>{unitCells(student, unit, true)}</React.Fragment>)}</tr>)}</tbody></table></div><p className="mt-3 text-[11px] text-slate-500">{summativeCompetencyKey}</p></div></div></div>}
    {returnDialog && <div className="fixed inset-0 z-[120] grid place-items-center bg-slate-950/55 p-4" role="dialog" aria-modal="true" aria-labelledby="return-result-title"><div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl"><div className="flex items-start justify-between border-b border-slate-200 px-5 py-4"><div className="flex items-start gap-3"><span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-rose-50 text-rose-600"><XCircle className="h-5 w-5" /></span><div><h2 id="return-result-title" className="text-base font-bold text-slate-950">Return result batch</h2><p className="mt-0.5 text-xs leading-5 text-slate-500">Send this consolidated result set back for correction before it moves to the next approval stage.</p></div></div><button type="button" onClick={() => setReturnDialog(null)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Close return dialog"><X className="h-5 w-5" /></button></div><div className="space-y-4 px-5 py-4"><div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900"><p className="font-semibold">{returnDialog.group.programName}</p><p className="mt-0.5 text-amber-800">Level {returnDialog.group.programLevel || '—'} · Module {returnDialog.group.moduleNumber || '—'} · {returnDialog.group.batches.length} unit{returnDialog.group.batches.length === 1 ? '' : 's'}</p></div><label className="block"><span className="flex items-center justify-between text-xs font-semibold text-slate-800"><span>Reason for return</span><span className="font-normal text-slate-400">Optional</span></span><textarea autoFocus value={returnReason} onChange={event => setReturnReason(event.target.value)} rows="4" placeholder="Describe what needs to be corrected…" className="mt-1.5 w-full resize-y rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-rose-400 focus:ring-4 focus:ring-rose-100" /></label></div><div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3"><button type="button" onClick={() => setReturnDialog(null)} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100">Cancel</button><button type="button" onClick={() => { const dialog = returnDialog; setReturnDialog(null); apply(dialog.group, dialog.action, returnReason); }} className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-700"><XCircle className="h-3.5 w-3.5" />Return for correction</button></div></div></div>}
  </div>;
};

export default SummativeResultApprovals;
